//@author: devin
import {
    ApplicationRef,
    ComponentRef,
    createComponent,
    EmbeddedViewRef,
    EnvironmentInjector,
    Injectable,
    Injector,
    NgZone,
} from "@angular/core";
import { jSith } from "../util/jquery-replacement";

export interface DomResp<Comp> {
    domElem?: HTMLElement;
    componentRef?: ComponentRef<any>;
    instance?: Comp;
    destroy: Function;
}

@Injectable({
    providedIn: "root",
})
export class DomService {
    constructor(
        private appRef: ApplicationRef,
        private injector: Injector,
        private environmentInjector: EnvironmentInjector,
        private zone: NgZone,
    ) {}

    getComponentRefAndElement(component: any, model?: any): Promise<DomResp<any>> {
        const defer = jSith.defer();

        //create a component reference from the component
        const componentRef = createComponent(component, {
            environmentInjector: this.environmentInjector,
            elementInjector: this.injector,
        });

        const instance: any = componentRef.instance;
        model &&
            instance &&
            jSith.forEach(model, (key: any, value: any) => {
                try {
                    // this will work for either annotated @Inputs or signal-based `input`s
                    // BUT it will not work if we were setting a property explicitly that is not actually an input
                    componentRef.setInput(key, value);
                } catch (e) {
                    // so we catch and fall back to the previous behavior for backwards compatibility
                    instance[key] = value;
                }
            });

        //attach component to the appRef so that it's inside the ng component tree
        this.appRef.attachView(componentRef.hostView);

        //get DOM element from component
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

        this.zone.run(() => {
            setTimeout(() => {
                const domResp: DomResp<any> = {
                    domElem: domElem,
                    componentRef: componentRef,
                    instance: instance,
                    destroy: () => {
                        componentRef && componentRef.hostView && this.appRef.detachView(componentRef.hostView);
                        componentRef && componentRef.destroy();
                    },
                };
                defer.resolve(domResp);
            }, 1);
        });

        return defer.promise;
    }

    appendComponentToTarget(component: any, target?: HTMLElement, model?: any): Promise<DomResp<any>> {
        target = target || document.body;
        return this.getComponentRefAndElement(component, model).then((domResp) => {
            target.appendChild(domResp.domElem); //append DOM element to the target
            return domResp;
        });
    }

    appendComponentToBody(component: any, model?: any): Promise<DomResp<any>> {
        return this.appendComponentToTarget(component, document.body, model);
    }
}
